import cx from 'classnames';

import { Icon } from '@sb/design-system';
import type { RestoreData } from '@sb/feathers-types';
import { Loader } from '@sb/ui/components';

import { RestoreAlert } from './RestoreAlert';
import { RestoreErrorDetail } from './RestoreErrorDetail';

interface RestoreProgressPanelProps {
  data: RestoreData;
}

interface RestoreStatusBlockProps {
  icon: React.ReactNode;
  title: string;
  children?: React.ReactNode;
  'data-testid': string;
}

function RestoreStatusBlock({
  icon,
  title,
  children,
  'data-testid': dataTestId,
}: RestoreStatusBlockProps) {
  return (
    <section
      // Centring is applied per child rather than on this container: the
      // alert rows inside align their own text, and a container-level
      // `tw-text-center` silently wins over their `tw-text-left` (both are
      // single-class utilities, so Tailwind's emit order decides).
      className={cx(
        'tw-flex',
        'tw-flex-col',
        'tw-items-center',
        'tw-gap-8',
        'tw-py-24',
        // Children are centred, but a full-width child (the error block)
        // must still be able to stretch.
        'tw-self-stretch',
        // The modal is a fixed height, so a short outcome would otherwise
        // sit against the top with a third of the dialog blank beneath it.
        // A flex item never shrinks below its content, so taller outcomes
        // still scroll rather than being clipped by the centring.
        'tw-flex-1',
        'tw-justify-center',
      )}
      data-testid={dataTestId}
    >
      {icon}
      <h4 className="tw-text-17 tw-font-medium tw-text-center">{title}</h4>
      {children}
    </section>
  );
}

/**
 * The restore is out of the operator's hands: applying, or finished.
 *
 * Mirrors what a software install shows on the robot — Marvin's restore
 * wizard literally promises the robot will "show progress on its screen, the
 * way a software update does".
 */
export function RestoreProgressPanel({ data }: RestoreProgressPanelProps) {
  if (data.state === 'confirmed') {
    return (
      <RestoreStatusBlock
        icon={<Loader />}
        title="Starting the restore…"
        data-testid="restore-confirmation-progress-region"
      >
        <p className="tw-text-13 tw-text-label-secondary tw-text-center">
          The robot picks the confirmation up on its next check-in. Leave it
          powered on.
        </p>
      </RestoreStatusBlock>
    );
  }

  if (data.state === 'declined') {
    return (
      <RestoreStatusBlock
        icon={<Icon kind="xmarkCircle" className="tw-icon-28" />}
        title="Restore declined"
        data-testid="restore-confirmation-declined-region"
      >
        <p className="tw-text-13 tw-text-label-secondary tw-text-center">
          Nothing on this robot was changed. This restore is finished — to run
          it after all, it has to be ordered again from Marvin.
        </p>
      </RestoreStatusBlock>
    );
  }

  if (data.state === 'running') {
    return (
      <RestoreStatusBlock
        icon={<Loader />}
        title="Restoring this robot…"
        data-testid="restore-confirmation-progress-region"
      >
        <RestoreAlert
          severity="critical"
          data-testid="restore-confirmation-power-warning"
        >
          Do not power the robot off. Services will restart on their own when
          the restore finishes.
        </RestoreAlert>

        <p className="tw-text-13 tw-text-label-secondary tw-text-center">
          This can take several minutes, and this screen may disconnect while
          the services restart.
        </p>
      </RestoreStatusBlock>
    );
  }

  const succeeded = data.state === 'succeeded';

  return (
    <RestoreStatusBlock
      icon={
        <Icon
          kind={succeeded ? 'checkmarkCircleFill' : 'exclamationTriangleFill'}
          className={cx(
            'tw-icon-28',
            succeeded ? 'tw-text-green' : 'tw-text-red',
          )}
        />
      }
      title={succeeded ? 'Restore complete' : 'Restore failed'}
      data-testid="restore-confirmation-result-region"
    >
      {/* A failure's reason is an engine error — long, path-heavy, and the
          first thing support asks for — so it gets a readable, copyable
          block rather than a line of centred prose. */}
      {data.error && !succeeded && <RestoreErrorDetail message={data.error} />}

      {/* On a success the note IS the model shortfall, which the report
          panel below states in full — repeating it here says it twice. */}
      {data.error && succeeded && !data.modelReport && (
        <p
          className="tw-text-13 tw-text-label-secondary tw-text-center"
          data-testid="restore-confirmation-result-message"
        >
          {data.error}
        </p>
      )}

      {succeeded && (
        <p className="tw-text-13 tw-text-label-secondary tw-text-center">
          This robot is now running the backup&apos;s routines, equipment and
          settings.
        </p>
      )}
    </RestoreStatusBlock>
  );
}
